import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import TableCard from '../../components/TableCard/TableCard';
import Pagination from '../../components/Pagination/Pagination';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import LoadingState from '../../components/LoadingState/LoadingState';
import { getReminderCampaigns } from '../../api/reminders';
import { useI18n } from '../../i18n/I18nContext';
import { errorText } from '../../utils/errorText';
import { unwrapList } from '../../utils/api';
import { formatDateTime, formatNumber, formatPercent } from '../../utils/datetime';
import { TASHKENT, conversionRate } from './reminderUi';
import { CAMPAIGN_STATUS_TONE, ChannelIcons, isInFlight } from './reminderView';

const PAGE_SIZE = 20;
// A message sent a moment ago is SCHEDULED for a second or two, then SENDING: look again until it is done.
const POLL_MS = 3000;
const MAX_POLLS = 20;

/** "Faol emaslar · 7 kun" · "Kursi yo'qlar" — who a campaign went to. */
export function audienceText(t, campaign) {
  const audience = campaign?.audience;
  if (!audience) return '—';
  const base = t(`reminders.audience.${audience}`);
  const days = campaign.filters?.inactiveDays;
  return audience === 'INACTIVE' && days ? `${base} · ${t('reminders.audience.days', { days })}` : base;
}

/**
 * "Tarix": every message the business sent or scheduled, newest first, with how many people it
 * reached, how many opened the link and how many then enrolled (or came back to a lesson).
 * A row opens the campaign's own page (/reminders/:id). The page header carries the one yellow
 * "Yangi xabar" button, so an empty history does not add a second.
 */
export default function HistoryTab({ active = true, reloadToken = 0 }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [state, setState] = useState({ phase: 'loading', items: [], meta: null, error: null });
  const polls = useRef(0);

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    setState((prev) => ({ ...prev, phase: prev.items.length > 0 ? 'refreshing' : 'loading', error: null }));
    getReminderCampaigns({ page, limit: PAGE_SIZE })
      .then((res) => {
        if (!cancelled) setState({ phase: 'ready', items: unwrapList(res), meta: res?.meta || null, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ phase: 'error', items: [], meta: null, error });
      });
    return () => { cancelled = true; };
  }, [active, page, reloadKey, reloadToken]);

  const pending = state.items.some((campaign) => isInFlight(campaign));
  useEffect(() => {
    if (!pending) {
      polls.current = 0;
      return undefined;
    }
    if (!active || polls.current >= MAX_POLLS) return undefined;
    const timer = setTimeout(() => {
      polls.current += 1;
      setReloadKey((k) => k + 1);
    }, POLL_MS);
    return () => clearTimeout(timer);
  }, [pending, active, state.items]);

  const refresh = () => {
    polls.current = 0;
    setReloadKey((k) => k + 1);
  };

  const { phase, items, meta, error } = state;
  const busy = phase === 'refreshing';

  if (phase === 'loading') return <LoadingState skeleton="table" rows={6} message={t('common.loading')} />;
  if (phase === 'error') {
    return error?.code === 403 ? (
      <PageEmptyState variant="forbidden" size="sm" />
    ) : (
      <PageEmptyState
        variant="error"
        size="sm"
        title={t('reminders.history.loadError')}
        description={errorText(error, '') || undefined}
        onRetry={refresh}
      />
    );
  }
  if (items.length === 0 && page === 1) {
    return (
      <PageEmptyState
        variant="inbox"
        size="sm"
        title={t('reminders.history.emptyTitle')}
        description={t('reminders.history.emptyDesc')}
      />
    );
  }

  const pagination = meta && meta.totalPages > 1 ? (
    <Pagination
      page={meta.page}
      totalPages={meta.totalPages}
      total={meta.total}
      limit={meta.limit || PAGE_SIZE}
      onPrev={() => setPage((p) => Math.max(1, p - 1))}
      onNext={() => setPage((p) => p + 1)}
      hideOnSinglePage
    />
  ) : null;

  const open = (id) => navigate(`/reminders/${id}`);

  return (
    <div className={`rem-history${busy ? ' is-busy' : ''}`} aria-busy={busy || undefined}>
      <div className="rem-history__bar">
        <button type="button" className="ds-btn ds-btn-ghost ds-btn-sm" onClick={refresh}>
          <RefreshCw aria-hidden="true" />
          {t('reminders.history.refresh')}
        </button>
      </div>
      <TableCard aria-label={t('reminders.history.title')} footer={pagination}>
        <table className="ui-table ui-table--stack rem-history__table">
          <thead>
            <tr>
              <th>{t('reminders.history.col.campaign')}</th>
              <th>{t('reminders.history.col.channels')}</th>
              <th>{t('reminders.history.col.status')}</th>
              <th className="ui-num">{t('reminders.history.col.recipients')}</th>
              <th className="ui-num">{t('reminders.history.col.sent')}</th>
              <th className="ui-num">{t('reminders.history.col.opened')}</th>
              <th className="ui-num">{t('reminders.history.col.converted')}</th>
              <th>{t('reminders.history.col.date')}</th>
              <th className="rem-col-author">{t('reminders.history.col.author')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((campaign) => {
              const stats = campaign.stats || {};
              const rate = conversionRate(campaign.recipients, stats.converted);
              const when = campaign.sentAt || campaign.scheduledAt || campaign.createdAt;
              return (
                <tr key={campaign.id} className="is-clickable" onClick={() => open(campaign.id)}>
                  <td>
                    <button
                      type="button"
                      className="rem-campaign"
                      onClick={(e) => { e.stopPropagation(); open(campaign.id); }}
                    >
                      <span className="rem-campaign__title">{campaign.title}</span>
                      <span className="rem-campaign__sub">{audienceText(t, campaign)}</span>
                    </button>
                  </td>
                  <td data-label={t('reminders.history.col.channels')}>
                    <ChannelIcons channels={campaign.channels} t={t} />
                  </td>
                  <td data-label={t('reminders.history.col.status')}>
                    <StatusBadge tone={CAMPAIGN_STATUS_TONE[campaign.status] || 'neutral'}>
                      {t(`reminders.status.${campaign.status}`)}
                    </StatusBadge>
                  </td>
                  <td className="ui-num" data-label={t('reminders.history.col.recipients')}>{formatNumber(campaign.recipients || 0)}</td>
                  <td className="ui-num" data-label={t('reminders.history.col.sent')}>{formatNumber(stats.sent || 0)}</td>
                  <td className="ui-num" data-label={t('reminders.history.col.opened')}>{formatNumber(stats.clicked || 0)}</td>
                  <td className="ui-num" data-label={t('reminders.history.col.converted')}>
                    <span>
                      {formatNumber(stats.converted || 0)}
                      <span className="rem-muted">{` (${formatPercent(rate)})`}</span>
                    </span>
                  </td>
                  <td data-label={t('reminders.history.col.date')} className="rem-nowrap">
                    {formatDateTime(when, { timeZone: TASHKENT })}
                  </td>
                  <td data-label={t('reminders.history.col.author')} className="rem-col-author">
                    {campaign.createdBy?.name || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableCard>
    </div>
  );
}
